import { html, nothing } from "lit";
import type { LlmStatsTabId } from "../controllers/llm-stats-scope.ts";

export const LLM_STATS_TABS: Array<{
  id: LlmStatsTabId;
  label: string;
  icon: string;
  desc: string;
}> = [
  {
    id: "connect",
    label: "Connect",
    icon: "⬡",
    desc: "Providers, API keys, and discovered models",
  },
  { id: "overview", label: "Overview", icon: "◈", desc: "Local capacity & model usage" },
  {
    id: "analytics",
    label: "Analytics",
    icon: "📊",
    desc: "Cost breakdown, trends & provider budgets",
  },
  { id: "fleet", label: "Fleet spend", icon: "⬡", desc: "Network node cost breakdown" },
  { id: "sessions", label: "Sessions", icon: "◈", desc: "Session-by-session token & cost" },
  { id: "trace", label: "Trace", icon: "◈", desc: "Agent cycle span trace for selected session" },
  {
    id: "local",
    label: "Local LLM",
    icon: "⬢",
    desc: "Ollama status, installed models, and read-only activity console",
  },
];

export type LlmStatsTabsProps = {
  activeTab: LlmStatsTabId;
  onTabChange: (tab: LlmStatsTabId) => void;
};

export function renderLlmStatsTabs(props: LlmStatsTabsProps) {
  return html`
    <div class="llm-stats-tabs-container">
      <div class="llm-stats-tabs-bar" role="navigation" aria-label="LLM Stats views">
        ${LLM_STATS_TABS.map(
          (tab) => html`
            <button
              type="button"
              role="tab"
              class="llm-stats-tab ${props.activeTab === tab.id ? "active" : ""}"
              aria-selected=${props.activeTab === tab.id}
              id="llm-stats-tab-${tab.id}"
              data-testid=${`llm-stats-tab-${tab.id}`}
              @click=${() => props.onTabChange(tab.id)}
            >
              <span class="llm-stats-tab-icon">${tab.icon}</span>
              <span class="llm-stats-tab-label">${tab.label}</span>
              ${
                props.activeTab === tab.id
                  ? html` <span class="llm-stats-tab-indicator"></span> `
                  : nothing
              }
            </button>
          `,
        )}
      </div>
    </div>

    <style>
      .llm-stats-tabs-container {
        border-bottom: 1px solid var(--border);
        padding: 0 0 0 0;
        background: var(--bg-accent);
      }

      .llm-stats-tabs-bar {
        display: flex;
        gap: 0;
        padding: 0;
        overflow-x: auto;
        scrollbar-width: none;
      }
      .llm-stats-tabs-bar::-webkit-scrollbar {
        display: none;
      }

      .llm-stats-tab {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 14px 22px;
        border: none;
        background: transparent;
        color: var(--muted);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: color 0.15s, background 0.15s;
        white-space: nowrap;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
      }

      .llm-stats-tab:hover:not(.active) {
        color: var(--text);
        background: var(--bg-hover);
      }

      .llm-stats-tab.active {
        color: var(--accent);
        border-bottom-color: var(--accent);
      }

      .llm-stats-tab-icon {
        font-size: 13px;
        opacity: 0.6;
        transition: opacity 0.15s;
      }

      .llm-stats-tab.active .llm-stats-tab-icon {
        opacity: 1;
      }

      .llm-stats-tab-label {
        letter-spacing: -0.01em;
      }

      .llm-stats-tab-indicator {
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--accent);
        border-radius: 2px 2px 0 0;
        box-shadow: 0 0 8px var(--accent-glow);
        animation: tab-indicator-in 0.2s var(--ease-out) both;
      }

      @keyframes tab-indicator-in {
        from { opacity: 0; transform: scaleX(0.6); }
        to { opacity: 1; transform: scaleX(1); }
      }

      /* Active fill background on tab */
      .llm-stats-tab.active::before {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 2px;
        background: var(--accent);
        border-radius: 2px;
        box-shadow: 0 0 8px var(--accent-glow);
      }
    </style>
    </div>
  `;
}
